import type { SubscriptionInterval, SubscriptionPlan } from '@cribbage/protocol';
import { ApiError, api } from '~/api/client.js';
import { platform } from '~/platform/index.js';

/**
 * The one seam every store action goes through.
 *
 * The Subscription screens (`ui/screens/settings/`) call these and nothing
 * else: they never talk to Stripe, StoreKit or Play Billing themselves. Each
 * answer is a result the screen states in text, and none of them is proof of
 * membership. Only `/me` says what a player owns, so a screen that hears
 * `verified` refreshes `/me` and believes that instead.
 *
 * The web sells through Stripe (`docs/dev/subscriptions.md`): buying and
 * managing leave for Stripe's own pages, and a plan change is a call to the
 * server. The apps answer `unavailable` until StoreKit and Play Billing are
 * wired, and the screens say the store is not open yet. A call that fails
 * answers `failed`; the server's reason stays in the network log, because
 * every refusal it sends means the page's state is stale, and the screens
 * re-read `/me` on their next visit. The one refusal a page answers at once
 * is `price_changed`, as `repriced`: the web price the page showed was
 * replaced, so it reads `/me` again and shows the new one. Keep the names and
 * shapes: the screens and their tests are written against them.
 */

/** How often a plan bills. The web sells yearly only; the apps sell both. */
export type BillingInterval = SubscriptionInterval;

/** Which store this device buys through: Stripe on the web, Apple on iOS, Google on Android. */
export type BillingStore = 'web' | 'apple' | 'google';

/**
 * What a store action came back with.
 *
 * - `redirected`: the page is leaving for the store (Stripe Checkout, the
 *   Customer Portal). The result arrives later, on `/me`.
 * - `verified`: the server has checked the purchase. Refresh `/me` before
 *   showing anything as owned.
 * - `pending`: the store took it but has not confirmed it yet.
 * - `canceled`: the player backed out. Nothing was charged.
 * - `repriced`: the price the page showed was replaced first. Nothing was
 *   charged; read `/me` again for the new price.
 * - `failed`: it did not go through.
 * - `unavailable`: this device cannot do it yet.
 */
export type PurchaseResult =
  | { status: 'redirected' }
  | { status: 'verified' }
  | { status: 'pending' }
  | { status: 'canceled' }
  | { status: 'repriced' }
  | { status: 'failed' }
  | { status: 'unavailable' };

/** What a restore found. `found` means the server verified it; refresh `/me` to see it. */
export type RestoreResult = 'found' | 'none' | 'pending' | 'failed' | 'unavailable';

const UNAVAILABLE: PurchaseResult = { status: 'unavailable' };

/** The store this device sells through, read from the platform the app runs on. */
export function billingStore(): BillingStore {
  const name = platform().name;
  return name === 'ios' ? 'apple' : name === 'android' ? 'google' : 'web';
}

const FAILED: PurchaseResult = { status: 'failed' };
const REPRICED: PurchaseResult = { status: 'repriced' };

/** A refusal the page answers: the shown price was replaced, or anything else. */
function refusal(error: unknown): PurchaseResult {
  return error instanceof ApiError && error.code === 'price_changed' ? REPRICED : FAILED;
}

/** Leave for a page Stripe serves. The answer comes back on `/me`, after its return URL. */
async function leaveFor(link: () => Promise<{ readonly url: string }>): Promise<PurchaseResult> {
  try {
    const { url } = await link();
    location.assign(url);
    return { status: 'redirected' };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * Buy one plan, billed at one interval. On the web that is Stripe Checkout,
 * at `price`, the `web_prices` id the page showed. A page that showed no web
 * price has nothing to buy, and reads `/me` again as if it were repriced.
 */
export function startCheckout(
  plan: SubscriptionPlan,
  interval: BillingInterval,
  price: string | undefined,
): Promise<PurchaseResult> {
  if (billingStore() !== 'web') return Promise.resolve(UNAVAILABLE);
  if (price === undefined) return Promise.resolve(REPRICED);
  return leaveFor(() => api.startSubscriptionCheckout(plan, interval, price));
}

/**
 * Move a member onto another plan or interval, through the store that bills
 * them, at `price`, the `web_prices` id the page showed. The server answers
 * once Stripe has the change: an upgrade is paid, a downgrade is booked for
 * renewal. An upgrade whose card needs the player (3D Secure) leaves for the
 * invoice page instead.
 */
export async function changePlan(
  plan: SubscriptionPlan,
  interval: BillingInterval,
  price: string | undefined,
): Promise<PurchaseResult> {
  if (billingStore() !== 'web') return UNAVAILABLE;
  if (price === undefined) return REPRICED;
  try {
    const answer = await api.changeSubscriptionPlan(plan, interval, price);
    // The card wants the player to approve the upgrade's charge: pay on the
    // invoice page, and the plan changes once they do.
    if ('url' in answer) {
      location.assign(answer.url);
      return { status: 'redirected' };
    }
    return { status: 'verified' };
  } catch (error) {
    return refusal(error);
  }
}

/** Open the store's own page for renewal, payment details and cancellation. */
export function openManage(): Promise<PurchaseResult> {
  if (billingStore() !== 'web') return Promise.resolve(UNAVAILABLE);
  return leaveFor(() => api.manageSubscription());
}

/**
 * Ask the store for a subscription bought with this account on another
 * device. A web purchase belongs to the account, so restoring it is reading
 * `/me` again.
 */
export async function restorePurchases(): Promise<RestoreResult> {
  if (billingStore() !== 'web') return 'unavailable';
  try {
    // Asked directly: the store's refresh keeps the cached `/me` when offline.
    const { subscription } = await api.me();
    return subscription && subscription.status !== 'expired' ? 'found' : 'none';
  } catch {
    return 'failed';
  }
}
